'use client';

import { usePostHog } from 'posthog-js/react';
import { useState, useEffect, useLayoutEffect } from 'react';
import { createContext, useContext } from 'react';

import { CookieConsentStatus } from '@hub/common/src/analytics-utils';

import { useCitThemeClient } from './CitThemeProvider';

const CookieConsentStatusContext = createContext<{
  hasConsent: CookieConsentStatus;
  setHasConsent: (hasConsent: CookieConsentStatus) => void;
  consentLoaded: boolean;
} | null>(null);

export function useCookieConsentStatus() {
  const context = useContext(CookieConsentStatusContext);
  if (!context) {
    throw new Error('useCookieConsentStatus must be used within a CookieConsentStatusProvider');
  }
  return context;
}

export default function CookieConsentStatusProvider({ children }: { children: React.ReactNode }) {
  const posthog = usePostHog();

  // a theme tester preview is the owner viewing their own site: send nothing and never touch consent
  const disabled = useCitThemeClient().accountTheme.isTestTheme();

  const [hasConsent, _setHasConsent] = useState<CookieConsentStatus>(CookieConsentStatus.PENDING);
  const [consentLoaded, setConsentLoaded] = useState(false);

  const setHasConsent = (hasConsent: CookieConsentStatus) => {
    if (hasConsent === CookieConsentStatus.GRANTED) {
      posthog.opt_in_capturing();
    } else if (hasConsent === CookieConsentStatus.DENIED) {
      posthog.opt_out_capturing();
    }
    _setHasConsent(hasConsent);
  };

  // drops every event in this page, including those not gated on consent; a layout effect so it lands
  // before any child's passive effect can capture
  useLayoutEffect(() => {
    if (!disabled) {
      return;
    }

    posthog.set_config({ before_send: () => null });

    return () => posthog.set_config({ before_send: undefined });
  }, [posthog, disabled]);

  useEffect(() => {
    // consentLoaded stays false, which keeps the banner, GA and every consent-gated capture off
    if (disabled) {
      return;
    }

    if (posthog.__loaded) {
      // eslint-disable-next-line
      _setHasConsent(posthog.get_explicit_consent_status());
      setConsentLoaded(true);
    }
  }, [posthog, disabled]);

  return (
    <CookieConsentStatusContext.Provider value={{ hasConsent, setHasConsent, consentLoaded }}>
      {children}
    </CookieConsentStatusContext.Provider>
  );
}
